import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { IProductInterface, IOptionsProduct } from "../models/products.model";
import {
  IShippingCartInterface,
  IStoreInterface,
} from "../models/store.interface";
import { IOrderInterface } from "../models/order.interface";

@Injectable({
  providedIn: "root",
})
export class CommerceService {
  constructor(private http: HttpClient) {}
  private storeSettinges$: BehaviorSubject<IStoreInterface> =
    new BehaviorSubject<IStoreInterface>(null);

  public setStore(store: IStoreInterface): void {
    this.storeSettinges$.next(store);
  }

  public getStoreCurrentSession(siteId?: string): Promise<IStoreInterface> {
    return new Promise((resolve, reject) => {
      if (this.storeSettinges$.getValue()) {
        resolve(this.storeSettinges$.getValue());
      } else {
        this.getStore(siteId).subscribe(
          (store) => {
            this.setStore(store.data);
            resolve(store.data);
          },
          (err) => {
            resolve(undefined);
          }
        );
      }
    });
  }

  public getProducts(
    siteId: string,
    categories?: string[]
  ): Observable<{ message: string; data: IProductInterface[] }> {
    return this.http.post<{ message: string; data: IProductInterface[] }>(
      `${environment.CommerceUrl}/publicProducts`,
      { siteId, categories },
      { headers: { Anonymous: "Anonymous" } }
    );
  }

  public getProduct(
    siteId: string,
    productId: string
  ): Observable<{ message: string; data: IProductInterface }> {
    return this.http.post<{ message: string; data: IProductInterface }>(
      `${environment.CommerceUrl}/getProduct`,
      { id: productId, siteId },
      { headers: { Anonymous: "Anonymous" } }
    );
  }

  public deleteProduct(
    siteId: string,
    productId: string
  ): Observable<{ message: string; data: IProductInterface }> {
    return this.http.post<{ message: string; data: IProductInterface }>(
      `${environment.CommerceUrl}/deleteProduct`,
      { id: productId, siteId }
    );
  }

  public updateProduct(
    product: IProductInterface,
    deletedImages,
    sortedImages
  ): Observable<{ message: string; data: IProductInterface }> {
    return this.http.put<{ message: string; data: IProductInterface }>(
      `${environment.CommerceUrl}/updateProduct`,
      { ...product, deletedImages, sortedImages }
    );
  }

  public createProduct(
    product: IProductInterface
  ): Observable<{ message: string; data: IProductInterface }> {
    return this.http.post<{ message: string; data: IProductInterface }>(
      `${environment.CommerceUrl}/createProduct`,
      { ...product }
    );
  }

  public saveStore(
    store: IStoreInterface
  ): Observable<{ message: string; data: any }> {
    return this.http.post<{ message: string; data: any }>(
      `${environment.CommerceUrl}/saveStore`,
      { ...store }
    );
  }

  public getStore(
    siteId: string
  ): Observable<{ message: string; data: IStoreInterface }> {
    return this.http.post<{ message: string; data: IStoreInterface }>(
      `${environment.CommerceUrl}/getStore`,
      { siteId },
      { headers: { Anonymous: "Anonymous" } }
    );
  }

  public trackOrder(
    siteId: string,
    orderId: string,
    email: string
  ): Observable<{
    message: string;
    data: IOrderInterface;
    products: IProductInterface[];
  }> {
    return this.http.post<{
      message: string;
      data: IOrderInterface;
      products: IProductInterface[];
    }>(`${environment.CommerceUrl}/trackOrder`, { siteId, id: orderId, email });
  }

  public addToCart(
    product: IProductInterface,
    quantity: number,
    optionSelected: IOptionsProduct
  ): void {
    if (localStorage.getItem("cart-checkout")) {
      const cart = JSON.parse(localStorage.getItem("cart-checkout"));
      cart.push({ product, quantity, optionSelected });
      localStorage.setItem("cart-checkout", JSON.stringify(cart));
    } else {
      this.setCart([{ product, quantity, optionSelected }]);
    }
  }

  public setCart(cart: IShippingCartInterface[]): void {
    localStorage.setItem("cart-checkout", JSON.stringify(cart));
  }

  public getCartItems(): IShippingCartInterface[] {
    return localStorage.getItem("cart-checkout")
      ? JSON.parse(localStorage.getItem("cart-checkout"))
      : [];
  }

  public removeCartItems(): void {
    localStorage.removeItem("cart-checkout");
  }

  public createOrder(
    order: IOrderInterface
  ): Observable<{ message: string; orderId: string }> {
    return this.http.post<{ message: string; orderId: string }>(
      `${environment.CommerceUrl}/createOrder`,
      { ...order },
      { headers: { Anonymous: "Anonymous" } }
    );
  }

  public createDemoProduct(siteId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.CommerceUrl}/createDemoProduct`,
      { siteId }
    );
  }
}
